import { forwardRef, type AnchorHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { useNavPrefs } from './nav-prefs-context';
import { matchNavTarget } from './nav-prefs-match';

type Props = Omit<LinkProps, 'to'> & {
  to: string;
  /** Force opt-out of the smart behaviour (rare — useful in dropdowns
   *  whose anchor would disappear if the click opened a new tab). */
  disableSmart?: boolean;
};

/** Drop-in replacement for react-router's `<Link>` that renders an
 *  `<a target="_blank">` when the user enabled the matching pref. Falls
 *  through to `<Link>` otherwise so React Router keeps handling SPA
 *  navigation, ctrl/cmd-click, and middle-click natively. */
export const SmartLink = forwardRef<HTMLAnchorElement, Props>(function SmartLink(
  { to, disableSmart, children, ...rest },
  ref,
) {
  const { shouldOpenInNewTab } = useNavPrefs();

  if (!disableSmart) {
    const match = matchNavTarget(to);
    if (match && shouldOpenInNewTab(match.entry.id, match.kind)) {
      const anchorProps = rest as AnchorHTMLAttributes<HTMLAnchorElement>;
      return (
        <a ref={ref} href={to} target="_blank" rel="noopener noreferrer" {...anchorProps}>
          {children}
        </a>
      );
    }
  }

  return <Link ref={ref} to={to} {...rest}>{children}</Link>;
});
